import { ConfigMarket } from '@App/redux/interfaces'
import { AppDispatch, RootState } from '@App/redux/store'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {getDataWrapper} from "@App/api/helper";
import {$host} from "@App/api";
import moment from "moment";
import {format} from "date-fns";
import {fromZonedTime} from "date-fns-tz";
import {ICalendarCountry} from "@Redux/slices/config/interfaces";

export type ImpactLevel = 'low' | 'medium' | 'high' | 'none'

type Country = {
  key: string
  name?: string
}

export type Metric = {
  actual: string | number | null
  forecast: string | number | null
  prior: string | number | null
  unit?: string
}

export interface TimestampInterval {
  timestampFrom: number | null
  timestampTo: number | null
}

interface CalendarState {
  events: CalendarEvent[];
  countries: ICalendarCountry[];
}

export type EventMarket = Pick<ConfigMarket, 'title' | 'id' | 'symbol' | 'target_currency_code' | 'source_currency_code'>

export type CalendarEvent = {
  id: number
  datetime: string
  date: string
  country: Country
  title: string
  isPast: boolean
  impact: ImpactLevel
  metric: Metric
  relatedMarkets: EventMarket[]
}

const impactLevels: ImpactLevel[] = ['none', 'low', 'medium', 'high'];

const makeMetric = (actual: any, forecast: any, prior: any, unit?: string) => ({
  actual,
  forecast,
  prior,
  unit,
})

export const getCalendarEvents = async (
  interval: TimestampInterval,
  countries?: string[],
  impacts?: ImpactLevel[],
) => {
  const { timestampFrom, timestampTo } = interval;

  const postData: Record<string, any> = {};

  if (typeof timestampFrom === 'number') {
    postData.timestampFrom = timestampFrom;
  }

  if (typeof timestampTo === 'number') {
    postData.timestampTo = timestampTo;
  }

  if (Array.isArray(countries) && countries.length > 0) {
    postData.countries = countries;
  }

  if (Array.isArray(impacts) && impacts.length > 0) {
    postData.impacts = impacts.map((val) => impactLevels.indexOf(val));
  }

  return getDataWrapper(
    $host.post('InternalApi/events', postData)
  );
};

const initialState: CalendarState = {
  events: [],
  countries: [],
};

export const asyncGetEvents = createAsyncThunk<
  CalendarEvent[],
  {
    from: Date;
    to?: Date;
    countries?: string[];
    impacts?: string[];
  },
  {
    state: RootState;
    dispatch: AppDispatch;
  }
>(
  'calendar/asyncGetEvents',
  async ({ from, to, countries, impacts }, { getState }) => {

    const timestampInterval: TimestampInterval = {
      timestampFrom: Math.round(from.getTime() / 1000),
      timestampTo: to ? Math.round(to.getTime() / 1000) : null,
    };

    const requestCountries =
      countries && countries.includes('all') ? undefined : countries;

    const requestImpacts =
      impacts && impacts.includes('all') ? undefined : impacts;

    const resp = await getCalendarEvents(
      timestampInterval,
      requestCountries,
      requestImpacts as ImpactLevel[]
    );

    if (typeof resp !== 'object' || resp == null || !('data' in resp)) {
      return [];
    }

    const state = getState();
    const allMarkets = state.config.markets;
    const allCountries = state.calendarEvents.countries;

    const events: CalendarEvent[] = [];

    for (const item of resp.data as any[]) {

      const country = allCountries.find(c => c.key === item.country_key);
      if (!country) continue;

      const relatedMarkets = allMarkets
        .filter(m => item.markets.includes(m.id))
        .map(m => ({
          id: m.id,
          title: m.title,
          source_currency_code: m.source_currency_code,
          symbol: m.symbol,
          target_currency_code: m.target_currency_code,
        }));

      const utcDate = fromZonedTime(item.time.date, item.time.timezone);
      const date = format(utcDate, 'yyyy-MM-dd');
      const datetime = utcDate.toISOString();

      events.push({
        id: item.id,
        date,
        datetime,
        country,
        title: item.event,
        isPast: utcDate < new Date(),
        impact: impactLevels[item.impact] ?? 'none',
        metric: makeMetric(
          item.actual,
          item.forecast,
          item.prior,
          ''
        ),
        relatedMarkets,
      });
    }

    return events;
  }
);

export const asyncGetAvailableCalendarCountries = createAsyncThunk<
  ICalendarCountry[],
  void
>(
  'calendar/asyncGetAvailableCalendarCountries',
  async () => {

    const resp = await getDataWrapper(
      $host.get('InternalApi/events/available-countries')
    );

    if (!resp || typeof resp !== 'object' || !('data' in resp)) {
      return [];
    }

    return resp.data as ICalendarCountry[];
  }
);

const calendarEventsSlice = createSlice({
  name: 'calendar-events',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(asyncGetEvents.fulfilled, (state, { payload }) => {
        state.events = payload;
      })
      .addCase(asyncGetAvailableCalendarCountries.fulfilled, (state, { payload }) => {
        state.countries = payload;
      });
  },
});

export default calendarEventsSlice.reducer
